// =====================================================================
// duplicateGuard — Phase 7D Duplicate Prevention Guardrail (PURE).
// =====================================================================
//
// Pre-save scan that runs BEFORE any new family_graph_members insert and
// returns a ranked list of "possible existing matches" with a confidence
// label (Strong / Likely / Possible). The caller (e.g. AddGraphMemberModal)
// is responsible for surfacing the confirmation modal — this module ONLY
// computes the candidate list and records a diagnostic event ring buffer
// owner / admin can review.
//
// CRITICAL DESIGN RULES — STRICTLY ADDITIVE, NEVER MUTATING:
//
//   • Pure read-only computation over the loaded `GraphMember[]`. No
//     Supabase calls, no DB writes, no schema changes, no relationship
//     mutations.
//   • NEVER auto-merges, NEVER deletes, NEVER overwrites existing
//     names / birthdays / photos / parent_ids / spouse_id /
//     claimed_by_user_id.
//   • Hard cap on graph size scanned (50_000 members) so the matcher
//     can never become a perf regression on enormous graphs.
//   • When the master `duplicateGuardrail` flag is OFF the matcher
//     returns an empty list — every existing add-member flow runs
//     byte-for-byte unchanged.
//   • Diagnostics are an in-memory ring buffer (max 64 events) plus a
//     best-effort localStorage mirror so an owner / admin can review
//     them on the diagnostics card. PII is intentionally minimal —
//     attempted name + matched name + confidence + action are recorded;
//     no auth uids, no emails, no birthdays, no photo URLs.
//
// PUBLIC API:
//
//   findDuplicateMatches(opts) → DuplicateMatch[]
//     Run the scan. Returns 0..N matches sorted highest-confidence first.
//     When `duplicateGuardrail` is OFF, returns [].
//
//   recordDuplicateEvent(event) → void
//     Append a diagnostic event to the ring buffer. Best-effort.
//
//   snapshotDuplicateEvents() → DuplicateEvent[]
//     Return the recorded events (most recent first). Owner/admin
//     diagnostics card consumes this.
//
//   clearDuplicateEvents() → void
//     Reset the ring buffer (used by the diagnostics card's "Clear"
//     button).

import type { GraphMember, Relationship, Side } from '@/contexts/FamilyGraphContext';
import { isDuplicateGuardrailEnabled } from '@/lib/ai/featureFlag';

// ── Types ──────────────────────────────────────────────────────────────────

export type DuplicateConfidence = 'strong' | 'likely' | 'possible';

export interface DuplicateMatch {
  /** The existing member this candidate references. */
  member: GraphMember;
  confidence: DuplicateConfidence;
  /** Display reasons that drove the confidence level. */
  reasons: string[];
  /** Numeric score 0..1 used purely for ordering (highest first). */
  score: number;
}

export interface DuplicateScanInput {
  /** Trimmed name the user typed in the form. */
  name: string;
  /** Optional birth year hint. */
  birthYear?: number | null;
  /** Optional ISO YYYY-MM-DD birth date hint. */
  birthDate?: string | null;
  /** Relationship the new row is being saved as. */
  relationship?: Relationship | null;
  /** Side of family. */
  side?: Side | null;
  /** Anchor relative chosen in the form (parent / spouse / sibling pivot). */
  anchorId?: string | null;
  /** Optional secondary anchor for dual-parent links. */
  secondAnchorId?: string | null;
  /** Optional email hint (used when invite acceptance binds an email). */
  email?: string | null;
  /** All members currently loaded in the family graph. */
  members: GraphMember[];
}

export interface DuplicateEvent {
  /** ISO timestamp the event was recorded. */
  at: string;
  /** Trimmed name the user attempted to save. */
  attemptedName: string;
  /** Display name of the matched existing member, when applicable. */
  matchedName: string | null;
  /** Stable id of the matched member (for log-correlation only — no PII). */
  matchedMemberId: string | null;
  confidence: DuplicateConfidence | null;
  /** Action the user took on the confirm modal. */
  action: 'used-existing' | 'created-anyway' | 'cancelled' | 'no-match';
  /** Origin of the add — useful for grouping events per surface. */
  source:
    | 'add-graph-member'
    | 'quick-add'
    | 'invite-acceptance'
    | 'onboarding-relative'
    | 'set-relationship'
    | 'unknown';
  /** Echo of the relationship the user picked. */
  relationship: Relationship | null;
}

// ── Private helpers ────────────────────────────────────────────────────────

const MAX_SCAN_MEMBERS = 50_000;
const MAX_RESULTS = 5;

const safeNorm = (s: string | null | undefined): string => {
  if (!s) return '';
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const tokens = (s: string | null | undefined): string[] =>
  safeNorm(s).split(' ').filter((t) => t.length >= 2);

const startsWithEither = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.length < 3 || b.length < 3) return false;
  return a.startsWith(b) || b.startsWith(a);
};

/** Returns an overlap signal between two name strings. */
const nameOverlap = (
  a: string,
  b: string,
): { exactFull: boolean; firstLastMatch: boolean; partialMatch: boolean } => {
  const an = safeNorm(a);
  const bn = safeNorm(b);
  if (!an || !bn) {
    return { exactFull: false, firstLastMatch: false, partialMatch: false };
  }
  if (an === bn) {
    return { exactFull: true, firstLastMatch: true, partialMatch: true };
  }
  const aT = tokens(a);
  const bT = tokens(b);
  if (aT.length === 0 || bT.length === 0) {
    return { exactFull: false, firstLastMatch: false, partialMatch: false };
  }
  // First + last token agreement (with short prefix tolerance for nicknames).
  const aFirst = aT[0];
  const aLast = aT[aT.length - 1];
  const bFirst = bT[0];
  const bLast = bT[bT.length - 1];
  const firstMatch = startsWithEither(aFirst, bFirst);
  const lastMatch = aLast === bLast;
  if (firstMatch && lastMatch) {
    return { exactFull: false, firstLastMatch: true, partialMatch: true };
  }
  // Fallback partial — at least 2 shared tokens (excluding single initials).
  const overlap = aT.filter((t) => bT.includes(t)).length;
  return {
    exactFull: false,
    firstLastMatch: false,
    partialMatch: overlap >= 2,
  };
};

const sameBirthDate = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  if (!a || !b) return false;
  return a.slice(0, 10) === b.slice(0, 10);
};

const sameBirthYear = (
  a: number | null | undefined,
  b: number | null | undefined,
): boolean => !!a && !!b && a === b;

const yearWithin = (
  a: number | null | undefined,
  b: number | null | undefined,
  delta: number,
): boolean => !!a && !!b && Math.abs(a - b) <= delta;

const sharesAnyId = (
  list: string[] | null | undefined,
  ids: Array<string | null | undefined>,
): boolean => {
  if (!list || list.length === 0) return false;
  for (const id of ids) {
    if (id && list.includes(id)) return true;
  }
  return false;
};

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Run the duplicate scan against the currently-loaded family graph.
 *
 * Confidence levels:
 *   • Strong   — exact normalized full name AND (same DOB OR same parent
 *                anchor OR same spouse anchor OR same claimed user).
 *   • Likely   — first+last token agreement AND a structural overlap (DOB
 *                within 3 yrs OR shared parent / spouse / child anchor)
 *                OR exact full-name match without a structural anchor.
 *   • Possible — soft overlap only: first+last name agreement, OR exact
 *                same DOB with a partial token overlap.
 *
 * NEVER mutates inputs. NEVER calls Supabase. Returns [] when the
 * `duplicateGuardrail` flag is OFF.
 */
export const findDuplicateMatchesAlways = (
  opts: DuplicateScanInput,
): DuplicateMatch[] => {
  const trimmed = (opts.name ?? '').trim();
  if (trimmed.length < 2) return [];
  const members = Array.isArray(opts.members) ? opts.members : [];
  if (members.length === 0) return [];

  const scan = members.length > MAX_SCAN_MEMBERS
    ? members.slice(0, MAX_SCAN_MEMBERS)
    : members;

  const anchorIds: Array<string | null | undefined> = [
    opts.anchorId ?? null,
    opts.secondAnchorId ?? null,
  ];
  const matches: DuplicateMatch[] = [];

  for (const m of scan) {
    if (!m || !m.id) continue;
    if (m.approvalStatus === 'rejected') continue;

    const overlap = nameOverlap(m.name ?? '', trimmed);
    if (!overlap.partialMatch) continue;

    const reasons: string[] = [];
    let score = 0;

    if (overlap.exactFull) {
      reasons.push('Exact full-name match');
      score += 0.45;
    } else if (overlap.firstLastMatch) {
      reasons.push('First and last name match');
      score += 0.3;
    } else {
      reasons.push('Partial name overlap');
      score += 0.15;
    }

    // ── Birthday signals ──────────────────────────────────────────────
    let dobSignal: 'exact' | 'year' | 'close' | 'none' = 'none';
    if (sameBirthDate(opts.birthDate ?? null, m.birthDate ?? null)) {
      dobSignal = 'exact';
      reasons.push('Same date of birth');
      score += 0.3;
    } else if (sameBirthYear(opts.birthYear ?? null, m.birthYear ?? null)) {
      dobSignal = 'year';
      reasons.push('Same birth year');
      score += 0.18;
    } else if (yearWithin(opts.birthYear ?? null, m.birthYear ?? null, 2)) {
      dobSignal = 'close';
      reasons.push('Birth year within 2 years');
      score += 0.06;
    } else if (
      opts.birthYear &&
      m.birthYear &&
      Math.abs(opts.birthYear - m.birthYear) > 5 &&
      !overlap.exactFull
    ) {
      // Strong negative — likely two different people sharing a name.
      continue;
    }

    // ── Structural anchor signals ─────────────────────────────────────
    let anchorSignal:
      | 'parent'
      | 'spouse'
      | 'child'
      | 'claimed-user'
      | 'none' = 'none';
    if (anchorIds.some((a) => !!a)) {
      const memberParents = m.parentIds ?? [];
      if (sharesAnyId(memberParents, anchorIds)) {
        anchorSignal = 'parent';
        reasons.push('Same parent anchor on the existing profile');
        score += 0.18;
      } else if (
        anchorIds.some((a) => !!a && m.spouseId === a)
      ) {
        anchorSignal = 'spouse';
        reasons.push('Already married to the chosen anchor');
        score += 0.22;
      } else if (
        anchorIds.some((a) => !!a) &&
        scan.some(
          (other) =>
            other.id !== m.id &&
            anchorIds.includes(other.id) &&
            (other.parentIds ?? []).includes(m.id),
        )
      ) {
        anchorSignal = 'child';
        reasons.push('Already a parent of the chosen anchor');
        score += 0.18;
      }
    }

    // ── Email / claim hint ────────────────────────────────────────────
    if (opts.email && m.claimedByUserId) {
      // We can't compare emails directly without auth lookups (which would
      // require Supabase). Instead, surface a soft signal: if a claim
      // exists AND the typed name closely matches the claimed profile,
      // flag it so the user is reminded the row is already owned.
      anchorSignal = anchorSignal === 'none' ? 'claimed-user' : anchorSignal;
      reasons.push('Existing profile is already claimed by a signed-in user');
      score += 0.12;
    }

    // ── Confidence classification ─────────────────────────────────────
    let confidence: DuplicateConfidence = 'possible';
    const hasStructuralAnchor = anchorSignal !== 'none';
    if (
      overlap.exactFull &&
      (dobSignal === 'exact' || dobSignal === 'year' || hasStructuralAnchor)
    ) {
      confidence = 'strong';
    } else if (
      overlap.firstLastMatch &&
      (dobSignal === 'exact' || dobSignal === 'year' || hasStructuralAnchor)
    ) {
      confidence = 'likely';
    } else if (overlap.exactFull) {
      // Exact name match but no other structural agreement — still likely.
      confidence = 'likely';
    } else {
      confidence = 'possible';
    }

    matches.push({
      member: m,
      confidence,
      reasons,
      score: Math.min(1, score),
    });
  }

  matches.sort((a, b) => {
    const order: Record<DuplicateConfidence, number> = {
      strong: 0,
      likely: 1,
      possible: 2,
    };
    if (order[a.confidence] !== order[b.confidence]) {
      return order[a.confidence] - order[b.confidence];
    }
    return b.score - a.score;
  });

  return matches.slice(0, MAX_RESULTS);
};

export const findDuplicateMatches = (
  opts: DuplicateScanInput,
): DuplicateMatch[] => {
  if (!isDuplicateGuardrailEnabled()) return [];

  return findDuplicateMatchesAlways(opts);
};

// ── Diagnostics ring buffer ───────────────────────────────────────────────

const RING_LIMIT = 64;
const LS_KEY = 'kinscircle.guardrail.duplicateEvents.v1';

const ring: DuplicateEvent[] = [];
let hydrated = false;

const hydrate = (): void => {
  if (hydrated) return;
  hydrated = true;
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      for (const e of parsed.slice(-RING_LIMIT)) {
        if (
          e &&
          typeof e.attemptedName === 'string' &&
          typeof e.action === 'string'
        ) {
          ring.push(e as DuplicateEvent);
        }
      }
    }
  } catch {
    /* non-fatal — ring stays empty */
  }
};

const persist = (): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(ring.slice(-RING_LIMIT)));
  } catch {
    /* non-fatal — quota / private mode */
  }
};

export const recordDuplicateEvent = (
  event: Omit<DuplicateEvent, 'at'> & { at?: string },
): void => {
  hydrate();
  const safe: DuplicateEvent = {
    at: event.at ?? new Date().toISOString(),
    attemptedName: (event.attemptedName ?? '').slice(0, 80),
    matchedName: event.matchedName
      ? event.matchedName.slice(0, 80)
      : null,
    matchedMemberId: event.matchedMemberId ?? null,
    confidence: event.confidence ?? null,
    action: event.action,
    source: event.source ?? 'unknown',
    relationship: event.relationship ?? null,
  };
  ring.push(safe);
  while (ring.length > RING_LIMIT) ring.shift();
  persist();
};

export const snapshotDuplicateEvents = (): DuplicateEvent[] => {
  hydrate();
  return ring.slice().reverse();
};

export const clearDuplicateEvents = (): void => {
  hydrate();
  ring.length = 0;
  persist();
};

/** Convenience — translate a confidence level to a human label. */
export const describeConfidence = (c: DuplicateConfidence): string => {
  if (c === 'strong') return 'Strong';
  if (c === 'likely') return 'Likely';
  return 'Possible';
};
